<script setup lang="ts">
/**
 * GridImageCards
 *
 * Grid of cards with images, headings, and descriptions.
 * Used for: Features, Products, Capabilities with visuals.
 *
 * Interaction Rules:
 * - WITH link: Lift + Shadow + Green Line + Image zoom ("Take me there")
 * - WITHOUT link: Shadow deepen + Image zoom ("Look at this")
 */
import type { GridImageCardsProps } from "~~/shared/types";

const props = withDefaults(defineProps<GridImageCardsProps>(), {
  columns: 4,
  sectionBg: "white",
  imageFit: "cover",
});

const { resolveLink } = useServiceRoutes();

const NuxtLink = resolveComponent("NuxtLink");

const bgClass = computed(() =>
  props.sectionBg === "neutral" ? "bg-neutral-50" : "bg-white"
);

const gridColsClass = computed(() => {
  switch (props.columns) {
    case 2:
      return "sm:grid-cols-2";
    case 3:
      return "sm:grid-cols-2 lg:grid-cols-3";
    case 4:
    default:
      return "sm:grid-cols-2 lg:grid-cols-4";
  }
});

const imageClass = computed(() =>
  props.imageFit === "contain"
    ? "size-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
    : "size-full object-cover transition-transform duration-500 group-hover:scale-105"
);
</script>

<template>
  <section :class="[bgClass, 'py-16 sm:py-24']">
    <div class="mx-auto max-w-7xl px-8">
      <!-- Header -->
      <div class="mb-12 text-center">
        <h2 class="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          {{ title }}
        </h2>
        <!-- eslint-disable vue/no-v-html -->
        <div
          v-if="subtitle"
          class="prose prose-lg text-lg text-left mt-8 max-w-7xl text-neutral-600"
          v-html="subtitle"
        ></div>
      </div>

      <!-- Cards Grid -->
      <div :class="['grid gap-8', gridColsClass]">
        <component
          :is="item.link ? NuxtLink : 'div'"
          v-for="(item, index) in items"
          :key="index"
          :to="item.link ? resolveLink(item.link) : undefined"
          class="group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition-all duration-300"
          :class="[
            item.link
              ? 'cursor-pointer hover:-translate-y-1 hover:shadow-lg'
              : 'hover:shadow-md',
          ]"
        >
          <!-- Image -->
          <div class="aspect-video overflow-hidden">
            <ResponsiveImage
              :src="item.image"
              :alt="item.alt || item.heading"
              :class="imageClass"
              loading="eager"
              :width="1200"
              :height="675"
            />
          </div>

          <!-- Content -->
          <div class="p-6">
            <h3 class="mb-2 text-lg font-bold text-slate-900">
              {{ item.heading }}
            </h3>

            <p v-if="item.description" class="text-sm leading-relaxed text-neutral-600">
              {{ item.description }}
            </p>

            <!-- Link Arrow (only for linked cards) -->
            <div
              v-if="item.link"
              class="mt-4 flex items-center gap-1 text-sm font-semibold text-primary-400"
            >
              <span>{{ item.link.text }}</span>
              <Icon
                name="lucide:arrow-right"
                size="14"
                class="transition-transform duration-300 group-hover:translate-x-1"
              />
            </div>
          </div>

          <!-- Hover Accent Line (only for linked cards) -->
          <div
            v-if="item.link"
            class="absolute bottom-0 left-0 right-0 h-1 origin-left scale-x-0 bg-primary-400 transition-transform duration-300 group-hover:scale-x-100"
          ></div>
        </component>
      </div>
    </div>
  </section>
</template>
